import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useNewOnboardingEligibility } from '../../hooks/useNewOnboardingEligibility';
import { ROUTES_PATH } from '../../constants/Routes';
import { useOabEligibility } from '../../services/oabEligibility';
import { professionToOnboardingBranch } from '../../services/tracking/onboarding/onboardingBranch';
import { UserPreferences } from '../../services/userPreferences/types';
import { OnboardingOfficeStatus } from './types';

export function useOnboardingOffice() {
  const history = useHistory();
  const {
    isEligible,
    baseEligible,
    completed: onboardingProfileCompleted,
    isSubscriptionLoading,
    isPollingCredits,
    flagResolved,
    isPreferencesLoading,
    isStudent,
    isStudentLoading,
    updateUserPreferencesAsync,
    preferences,
  } = useNewOnboardingEligibility('onboardingProfileCompleted');

  const oabSkipped = Boolean((preferences as UserPreferences).onboardingOabSkipped);
  const oabCompleted = Boolean(
    (preferences as UserPreferences).onboardingOabCompleted,
  );
  const photoCompleted = Boolean(
    (preferences as UserPreferences).onboardingPhotoCompleted,
  );
  const toolsCompleted = Boolean(
    (preferences as UserPreferences).onboardingToolsCompleted,
  );
  const stepCompleted = oabSkipped
    ? photoCompleted || onboardingProfileCompleted
    : onboardingProfileCompleted;
  const { profession, isEligibilityResolved } = useOabEligibility({ enabled: baseEligible });
  const oabStepDone = oabCompleted || oabSkipped || profession === 'OTHER';

  const status: OnboardingOfficeStatus = useMemo(() => {
    if (
      isStudentLoading ||
      isSubscriptionLoading ||
      !flagResolved ||
      isPreferencesLoading
    ) {
      return 'loading';
    }

    if (stepCompleted) {
      return 'completed';
    }

    // Aluno nunca faz a OAB: sem isto ficaria preso em prior_pending.
    if (isStudent) {
      return 'ineligible';
    }

    if (!oabStepDone) {
      // Quem acabou de responder "não sou advogado" chega aqui com o refetch da
      // elegibilidade em voo e a profissão antiga no cache: devolvê-lo à OAB
      // agora o faria cair no dashboard quando a resposta chegasse.
      return baseEligible && !isEligibilityResolved
        ? 'loading'
        : 'prior_pending';
    }

    if (!isEligible) {
      return 'ineligible';
    }

    return 'ready';
  }, [
    isStudentLoading,
    isSubscriptionLoading,
    flagResolved,
    isPreferencesLoading,
    stepCompleted,
    isStudent,
    oabStepDone,
    baseEligible,
    isEligibilityResolved,
    isEligible,
  ]);

  const markCompleted = useCallback(async () => {
    await updateUserPreferencesAsync({ onboardingProfileCompleted: true });
    history.push(ROUTES_PATH.ONBOARDING.TOOLS);
  }, [updateUserPreferencesAsync, history]);

  return {
    status,
    isPollingCredits,
    markCompleted,
    oabSkipped,
    toolsCompleted,
    isEligibilityResolved,
    branch: professionToOnboardingBranch(profession),
  };
}
